import { buildAgentsSearchUrl, marketplaceRoleLabel } from "~/lib/marketplace/constants";
import type { IndustryVertical } from "~/lib/utils/industry-profile";

/** Home page category pills — mapped to SIC-derived industry verticals. */
export const MOONLIGHT_CATEGORIES = [
  { id: "all", label: "All", verticals: [] as IndustryVertical[] },
  { id: "healthcare", label: "Healthcare", verticals: ["healthcare"] as IndustryVertical[] },
  { id: "real_estate", label: "Real Estate", verticals: ["real_estate"] as IndustryVertical[] },
  { id: "home_services", label: "Home Services", verticals: ["home_services"] as IndustryVertical[] },
  { id: "legal", label: "Legal", verticals: ["legal"] as IndustryVertical[] },
  { id: "automotive", label: "Automotive", verticals: ["automotive"] as IndustryVertical[] },
  { id: "fitness", label: "Fitness", verticals: ["fitness"] as IndustryVertical[] },
  { id: "restaurant", label: "Restaurant", verticals: ["hospitality"] as IndustryVertical[] },
  { id: "financial", label: "Financial", verticals: ["financial"] as IndustryVertical[] },
  { id: "ecommerce", label: "E-commerce", verticals: ["retail"] as IndustryVertical[] },
  { id: "education", label: "Education", verticals: ["education"] as IndustryVertical[] },
] as const;

export type MoonlightCategoryId = (typeof MOONLIGHT_CATEGORIES)[number]["id"];

export const MOONLIGHT_SORT_OPTIONS = [
  { id: "rating", label: "Highest Rated" },
  { id: "featured", label: "Most Cloned" },
  { id: "newest", label: "Newest" },
] as const;

export type MoonlightSortId = (typeof MOONLIGHT_SORT_OPTIONS)[number]["id"];

export const MOONLIGHT_HERO_SEARCH_EXAMPLES = [
  "dental receptionist Florida",
  "physical therapy scheduling",
  "legal intake specialist",
  "HVAC appointment setter",
];

export const MOONLIGHT_HERO_JOB_TITLES = [
  "Front Desk Receptionist",
  "Online Patient Coordinator",
  "Online Support Agent",
  "Inbound Sales Rep",
  "After Hours Receptionist",
] as const;

export const MOONLIGHT_POPULAR_RESPONSIBILITIES = [
  {
    value: "live_chat",
    label: "Live Chat",
    headerClass: "bg-blue-900",
    bodyClass: "bg-blue-50",
  },
  {
    value: "phone_calls",
    label: "Phone Calls",
    headerClass: "bg-emerald-900",
    bodyClass: "bg-emerald-50",
  },
  {
    value: "text_messages",
    label: "Text Messages",
    headerClass: "bg-sky-900",
    bodyClass: "bg-sky-50",
  },
  {
    value: "email",
    label: "Email",
    headerClass: "bg-indigo-900",
    bodyClass: "bg-indigo-50",
  },
  {
    value: "browser_calls",
    label: "Voice (WebRTC)",
    headerClass: "bg-cyan-900",
    bodyClass: "bg-cyan-50",
  },
  {
    value: "video_calls",
    label: "Video Calls",
    headerClass: "bg-violet-900",
    bodyClass: "bg-violet-50",
  },
  {
    value: "welcome_video",
    label: "Welcome Video",
    headerClass: "bg-fuchsia-900",
    bodyClass: "bg-fuchsia-50",
  },
  {
    value: "whatsapp",
    label: "WhatsApp",
    headerClass: "bg-green-800",
    bodyClass: "bg-green-50",
  },
  {
    value: "messenger",
    label: "Messenger",
    headerClass: "bg-blue-800",
    bodyClass: "bg-blue-50",
  },
] as const;

export const MOONLIGHT_POPULAR_TOOLS = [
  {
    value: "google_calendar",
    label: "Google Calendar",
    headerClass: "bg-blue-900",
    bodyClass: "bg-blue-50",
  },
  {
    value: "calendly",
    label: "Calendly",
    headerClass: "bg-indigo-900",
    bodyClass: "bg-indigo-50",
  },
  {
    value: "zoom",
    label: "Zoom",
    headerClass: "bg-sky-900",
    bodyClass: "bg-sky-50",
  },
  {
    value: "juvonno",
    label: "Juvonno",
    headerClass: "bg-teal-900",
    bodyClass: "bg-teal-50",
  },
  {
    value: "nexhealth",
    label: "NexHealth",
    headerClass: "bg-emerald-900",
    bodyClass: "bg-emerald-50",
  },
  {
    value: "dentrix",
    label: "Dentrix",
    headerClass: "bg-slate-800",
    bodyClass: "bg-slate-100",
  },
] as const;

export function popularResponsibilityAgentsUrl(
  responsibilityValue: string,
): string {
  return buildAgentsSearchUrl({ responsibilities: [responsibilityValue] });
}

export function popularToolsAgentsUrl(toolValue: string): string {
  return buildAgentsSearchUrl({ tools: [toolValue] });
}

export const INDUSTRY_EMOJI: Record<string, string> = {
  dental: "🦷",
  legal: "⚖️",
  home_services: "🏠",
  physical_therapy: "💪",
  chiropractic: "🦴",
  medical: "🏥",
  other: "✨",
};

export const INDUSTRY_BANNER: Record<string, string> = {
  dental: "from-sky-600 to-blue-800",
  legal: "from-slate-700 to-slate-900",
  home_services: "from-amber-600 to-orange-800",
  physical_therapy: "from-emerald-600 to-teal-800",
  chiropractic: "from-blue-600 to-blue-800",
  medical: "from-cyan-600 to-blue-900",
  other: "from-blue-600 to-blue-900",
};

export type MoonlightAgentListItem = {
  id: string;
  title: string;
  listingType: string;
  industry: string | null;
  vertical: IndustryVertical;
  role: string | null;
  skills: string[];
  featured: boolean;
  profilePath: string;
  websiteUrl: string | null;
  videoThumbnailUrl: string | null;
  videoCallEnabled: boolean;
  responsibilities: string[];
  tools: string[];
  aiAgent: {
    name: string;
    jobTitle: string | null;
    base64Avatar: string | null;
    emailAddress: string | null;
    phoneNumber: string | null;
    websiteUrl: string | null;
  } | null;
  channels: Record<string, boolean>;
};

export function marketplaceAgentProfilePath(projectId: string): string {
  return `/agents/${projectId}`;
}

export function agentsForCategory(
  agents: MoonlightAgentListItem[],
  categoryId: MoonlightCategoryId,
): MoonlightAgentListItem[] {
  if (categoryId === "all") return agents;
  const cat = MOONLIGHT_CATEGORIES.find((c) => c.id === categoryId);
  if (!cat?.verticals.length) return agents;
  return agents.filter((a) => cat.verticals.includes(a.vertical));
}

export function sortMoonlightAgents(
  agents: MoonlightAgentListItem[],
  sortId: MoonlightSortId,
): MoonlightAgentListItem[] {
  const list = [...agents];
  switch (sortId) {
    case "featured":
      return list.sort(
        (a, b) => Number(b.featured) - Number(a.featured) || a.title.localeCompare(b.title),
      );
    case "rating":
      return list.sort((a, b) => {
        const score = (x: MoonlightAgentListItem) =>
          x.listingType === "OFFICIAL" ? 2 : x.featured ? 1 : 0;
        return score(b) - score(a);
      });
    case "newest":
    default:
      return list;
  }
}

/** @deprecated Category filtering uses SIC vertical client-side; no single industry API param. */
export function industryParamForCategory(
  _categoryId: MoonlightCategoryId,
): string | undefined {
  return undefined;
}

export function filterAgentsByJobTitles<
  T extends {
    aiAgent?: { jobTitle?: string | null } | null;
    title?: string;
    role?: string | null;
  },
>(agents: T[], jobTitles: string[]): T[] {
  if (!jobTitles.length) return agents;
  return agents.filter((agent) =>
    jobTitles.some((title) => {
      const haystacks = [
        agent.aiAgent?.jobTitle ?? "",
        agent.title ?? "",
        agent.role ? marketplaceRoleLabel(agent.role) : "",
      ].map((s) => s.toLowerCase());
      const q = title.toLowerCase().trim();
      const tokens = q.split(/\s+/).filter((token) => token.length > 2);
      return haystacks.some((job) => {
        if (!job) return false;
        if (job.includes(q) || q.includes(job)) return true;
        if (!tokens.length) return false;
        const matched = tokens.filter((token) => job.includes(token));
        return matched.length >= Math.max(1, tokens.length - 1);
      });
    }),
  );
}

export function buildMoonlightAgentsUrl(opts: {
  search?: string;
  categoryId?: MoonlightCategoryId;
  jobTitles?: string[];
  responsibilities?: string[];
  tools?: string[];
  listingType?: "MOONLIGHT" | "OFFICIAL" | "ALL";
}) {
  return buildAgentsSearchUrl({
    search: opts.search,
    category: opts.categoryId,
    jobTitles: opts.jobTitles,
    responsibilities: opts.responsibilities,
    tools: opts.tools,
    listingType: opts.listingType,
  });
}

export function categoryFromParam(value: string | null | undefined): MoonlightCategoryId {
  if (!value) return "all";
  const match = MOONLIGHT_CATEGORIES.find((c) => c.id === value);
  return match?.id ?? "all";
}

/** Map legacy ?industry=dental URL params to home page category pills. */
export function categoryFromLegacyIndustry(industry: string): MoonlightCategoryId {
  if (industry === "all" || industry === "other") return "all";
  if (["dental", "medical", "physical_therapy", "chiropractic"].includes(industry)) {
    return "healthcare";
  }
  if (industry === "legal") return "legal";
  if (industry === "home_services") return "home_services";
  return "all";
}
